import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session } from "@supabase/supabase-js";
import { fetchUserRole, WrongPortalError } from "@/lib/auth/portalGuard";
import type { ClientUser } from "../types";

/**
 * Client auth — backed by real Supabase auth.
 *
 * A client is a normal `auth.users` identity tagged with the `client` role in
 * `user_roles` (see migration 20260521120000). Their org/chapter/phone/address
 * live in `public.client_profiles`, created automatically by the
 * `handle_new_user` trigger from the signup metadata.
 *
 * NOTE: `client_profiles` isn't in the generated `Database` types until someone
 * runs `npm run db:types:dev` after applying the migration — so the table is
 * queried through a small cast (`fromClientProfiles`). Replace with the typed
 * accessor once types are regenerated.
 */

/** Loosely-typed accessor for the not-yet-in-generated-types table. */
const fromClientProfiles = () =>
  (supabase.from as unknown as (t: string) => ReturnType<typeof supabase.from>)(
    "client_profiles",
  );

interface ClientProfileRow {
  id: string;
  full_name: string;
  organization_name: string;
  chapter_name: string;
  phone: string;
  address: string;
  created_at: string;
}

export interface SignupInput {
  fullName: string;
  email: string;
  phone: string;
  /** SMS-consent checkbox from step 1. */
  smsConsent: boolean;
  /** Chosen (or custom) school name. */
  school: string;
  /** True when `school` was typed via "Other" (→ create a schools row). */
  schoolIsCustom: boolean;
  /** Whether the client is a Greek org or a club. */
  orgType: "greek" | "club";
  /** Greek org name (when orgType==='greek'). */
  organizationName: string;
  /** True when `organizationName` was typed via "Other" (→ create org row). */
  organizationIsCustom: boolean;
  /** Club name (when orgType==='club'). */
  clubName: string;
  /** True when `clubName` was typed via "Other" (→ create a club row). */
  clubIsCustom: boolean;
  password: string;
}

/** Split a full name into first/last for the staff `profiles` row. */
const splitName = (fullName: string): { first: string; last: string } => {
  const parts = fullName.trim().split(/\s+/);
  return {
    first: parts[0] ?? "",
    last: parts.length > 1 ? parts.slice(1).join(" ") : "",
  };
};

/** Map a Supabase session + client_profiles row into our ClientUser shape. */
const toClientUser = (
  session: Session,
  profile: ClientProfileRow | null,
): ClientUser => ({
  id: session.user.id,
  email: session.user.email ?? "",
  fullName:
    profile?.full_name ||
    (session.user.user_metadata?.full_name as string) ||
    "",
  organizationName:
    profile?.organization_name ||
    (session.user.user_metadata?.organization_name as string) ||
    "",
  chapterName:
    profile?.chapter_name ||
    (session.user.user_metadata?.chapter_name as string) ||
    "",
  school: (session.user.user_metadata?.school as string) || "",
  phone: profile?.phone || (session.user.user_metadata?.phone as string) || "",
  address:
    profile?.address || (session.user.user_metadata?.address as string) || "",
  createdAt: profile?.created_at || session.user.created_at,
});

const fetchClientProfile = async (
  userId: string,
): Promise<ClientProfileRow | null> => {
  const { data } = await fromClientProfiles()
    .select("*")
    .eq("id", userId)
    .maybeSingle();
  return (data as unknown as ClientProfileRow | null) ?? null;
};

export const useClientAuth = () => {
  const [currentUser, setCurrentUser] = useState<ClientUser | null>(null);
  const [loading, setLoading] = useState(true);

  /** Hydrate from any existing session, then subscribe to auth changes. */
  useEffect(() => {
    let active = true;

    const hydrate = async (session: Session | null) => {
      if (!session) {
        if (active) {
          setCurrentUser(null);
          setLoading(false);
        }
        return;
      }
      /**
       * The session is shared with the internal portal — a staff member may
       * hold one. Only treat it as a CLIENT session if the role says so;
       * otherwise the client portal must not consider them authenticated
       * (and we leave the session intact for the internal portal).
       */
      const role = await fetchUserRole(session.user.id);
      if (!active) return;
      if (role !== "client") {
        setCurrentUser(null);
        setLoading(false);
        return;
      }
      const profile = await fetchClientProfile(session.user.id);
      if (active) {
        setCurrentUser(toClientUser(session, profile));
        setLoading(false);
      }
    };

    supabase.auth.getSession().then(({ data }) => hydrate(data.session));

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      hydrate(session);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  const signup = useCallback(async (input: SignupInput): Promise<ClientUser> => {
    const email = input.email.trim().toLowerCase();
    if (input.password.length < 6) {
      throw new Error("Password must be at least 6 characters.");
    }
    const { first, last } = splitName(input.fullName);

    /** The org name shown on the profile is the Greek org OR the club name. */
    const orgDisplay =
      input.orgType === "club"
        ? input.clubName.trim()
        : input.organizationName.trim();
    /** Chapter label combines org + school for staff readability. */
    const chapter = [orgDisplay, input.school.trim()]
      .filter(Boolean)
      .join(" — ");

    const { data, error } = await supabase.auth.signUp({
      email,
      password: input.password,
      options: {
        data: {
          account_type: "client",
          role: "client",
          first_name: first,
          last_name: last,
          full_name: input.fullName.trim(),
          organization_name: orgDisplay,
          club_name: input.orgType === "club" ? input.clubName.trim() : "",
          chapter_name: chapter,
          school: input.school.trim(),
          phone: input.phone.trim(),
          sms_consent: input.smsConsent,
          /** Tell the trigger to create new School/Org rows when typed via "Other" */
          new_school_name: input.schoolIsCustom ? input.school.trim() : "",
          new_org_name:
            input.orgType === "greek" && input.organizationIsCustom
              ? input.organizationName.trim()
              : "",
          new_club_name:
            input.orgType === "club" && input.clubIsCustom
              ? input.clubName.trim()
              : "",
        },
        emailRedirectTo: `${window.location.origin}/client/login`,
      },
    });

    if (error) {
      if (/already registered|already.*exists/i.test(error.message)) {
        throw new Error("An account with this email already exists.");
      }
      throw new Error(error.message);
    }

    /** If email confirmation is OFF, signUp returns a session and we're in. */
    if (data.session) {
      const profile = await fetchClientProfile(data.session.user.id);
      const user = toClientUser(data.session, profile);
      setCurrentUser(user);
      return user;
    }

    /** Email confirmation is ON — no session yet. */
    throw new Error(
      "Account created — please check your email to confirm before signing in.",
    );
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<ClientUser> => {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });
      if (error) {
        if (/email not confirmed/i.test(error.message)) {
          throw new Error("Please confirm your email before signing in.");
        }
        throw new Error("Incorrect email or password.");
      }
      if (!data.session) throw new Error("Could not start a session.");

      /**
       * Reject staff accounts on the client portal. This is an ACTIVE login
       * attempt (the user typed credentials into the client form), so reject it
       * cleanly: sign out so no session lingers — otherwise the account stays
       * authenticated and "logged in" despite the warning, which is exactly the
       * wrong-portal confusion. (The passive case — a staff member who already
       * holds an internal session and merely navigates here — is handled by the
       * role-aware `hydrate` above, which does NOT sign out.)
       */
      const role = await fetchUserRole(data.session.user.id);
      if (role !== "client") {
        await supabase.auth.signOut();
        throw new WrongPortalError("staff");
      }

      const profile = await fetchClientProfile(data.session.user.id);
      const user = toClientUser(data.session, profile);
      setCurrentUser(user);
      return user;
    },
    [],
  );

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setCurrentUser(null);
  }, []);

  return {
    currentUser,
    isAuthenticated: !!currentUser,
    loading,
    signup,
    login,
    logout,
  };
};

export type ClientAuthApi = ReturnType<typeof useClientAuth>;
